import React, { FunctionComponent, useCallback, useContext, useMemo } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import Translate from 'ui/atoms/translate';
import DateAtom from 'ui/atoms/date';
import { PaginationProps } from 'ui/molecules/pagination';
import {
  AdminApi,
  AdminPayoutEvent,
  AdminPayoutEventsListOrderingEnum,
  AdminPayoutEventsValuesRetrieveFieldEnum,
  PayoutEventTypeEnum,
  ProductPermissionEnum,
} from 'src/api';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import Button from 'src/ui/atoms/button';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Tooltip from 'src/ui/atoms/tooltip';
import { I18NContext } from 'src/ui/i18n/provider';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

export interface PayoutEventsTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  payoutEvents?: AdminPayoutEvent[];
  paginationProps?: PaginationProps;
  loading?: boolean;
  hasPermission: boolean;
  onEditButtonClick: (payoutEvent: AdminPayoutEvent) => void;
  onDeleteButtonClick: (payoutEvent: AdminPayoutEvent) => void;
  setFilterPopupHeight?: (height: number) => void;
}

const PayoutEventsTable: FunctionComponent<PayoutEventsTableProps> = (props) => {
  const {
    className,
    hasPermission,
    loading,
    ordering,
    onOrderBy,
    payoutEvents,
    paginationProps,
    onEditButtonClick,
    onDeleteButtonClick,
    setFilterPopupHeight,
  } = props;

  const { activeLocale } = useContext(I18NContext);

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const isWithinOneDayOfPayment = useCallback((paymentDate: Date) => {
    const oneDayBefore = new Date(new Date(paymentDate).getTime() - 24 * 60 * 60 * 1000);
    return new Date() >= oneDayBefore;
  }, []);

  const tokenHasManagePayoutPermission = (permissions: ProductPermissionEnum[]) =>
    permissions.includes(ProductPermissionEnum.MANAGE_PAYOUT_EVENTS);

  const payoutEventsOptions = useMemo(() => {
    return Object.values(PayoutEventTypeEnum).map((eventType) => ({
      id: eventType,
      name: translate(`studioPayoutEvents.payoutEventTypes.${snakeToCamelCaseWithNumbers(eventType)}`),
    }));
  }, [translate]);

  return (
    <div className={cx('payout-events-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={payoutEvents}
        paginationProps={paginationProps}
        loading={loading}
        hasPermission={hasPermission}
        setFilterPopupHeight={setFilterPopupHeight}
        cells={[
          {
            title: <Translate name="studioPayoutEventsTable.product" />,
            render: ({ productName }) => productName,
            orderingFieldName: AdminPayoutEventsListOrderingEnum.PRODUCT_NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'productName',
            searchFieldName: AdminPayoutEventsValuesRetrieveFieldEnum.PRODUCT_NAME,
            getFilterData: (params: any) => adminApi.adminPayoutEventsValuesRetrieve(params),
          },
          {
            title: <Translate name="studioPayoutEventsTable.issuer" />,
            render: ({ issuer }) => issuer,
            orderingFieldName: AdminPayoutEventsListOrderingEnum.ISSUER_ASC,
            type: CellTypeEnum.text,
            filterName: 'issuerName',
            searchFieldName: AdminPayoutEventsValuesRetrieveFieldEnum.ISSUER,
            getFilterData: (params: any) => adminApi.adminPayoutEventsValuesRetrieve(params),
          },
          {
            title: <Translate name="studioPayoutEventsTable.eventType" />,
            render: ({ payoutEventType }) => (
              <Translate name={`studioPayoutEvents.payoutEventTypes.${snakeToCamelCaseWithNumbers(payoutEventType)}`} />
            ),
            orderingFieldName: AdminPayoutEventsListOrderingEnum.PAYOUT_EVENT_TYPE_ASC,
            type: CellTypeEnum.text,
            filterName: 'payoutEventType',
            getFilterData: () => {
              return payoutEventsOptions;
            },
          },
          {
            title: <Translate name="studioPayoutEventsTable.paymentDate" />,
            render: ({ paymentDate }) => (
              <DateAtom showTime>
                {(() => {
                  const dateWithTime = new Date(paymentDate);
                  dateWithTime.setHours(23, 59, 0, 0);
                  return dateWithTime;
                })()}
              </DateAtom>
            ),
            orderingFieldName: AdminPayoutEventsListOrderingEnum.PAYMENT_DATE_ASC,
            type: CellTypeEnum.date,
            filterName: 'paymentDate',
          },
          {
            title: <Translate name="studioPayoutEventsTable.actionButtons" />,
            render: (payoutEvent) => (
              <ActionButtons align="start" spacingTop="none">
                {!isWithinOneDayOfPayment(payoutEvent.paymentDate) &&
                  tokenHasManagePayoutPermission(payoutEvent.productPermissions) && (
                    <Button variant="link" onClick={() => onDeleteButtonClick(payoutEvent)} size="xsmall">
                      <Translate name="common.delete" />
                    </Button>
                  )}
                {(isWithinOneDayOfPayment(payoutEvent.paymentDate) ||
                  !tokenHasManagePayoutPermission(payoutEvent.productPermissions)) && (
                  <Tooltip
                    maxWidth={250}
                    content={
                      !tokenHasManagePayoutPermission(payoutEvent.productPermissions) ? (
                        <Translate name="studioPayoutEventsTable.noDeletePermission" />
                      ) : (
                        <Translate name="studioPayoutEventsTable.paymentDatePassedDelete" />
                      )
                    }
                  >
                    <Button variant="link" size="xsmall" disabled={true}>
                      <Translate name="common.delete" />
                    </Button>
                  </Tooltip>
                )}
                {!isWithinOneDayOfPayment(payoutEvent.paymentDate) &&
                  tokenHasManagePayoutPermission(payoutEvent.productPermissions) && (
                    <Button variant="secondary" onClick={() => onEditButtonClick(payoutEvent)} size="xsmall">
                      <Translate name="common.edit" />
                    </Button>
                  )}
                {(isWithinOneDayOfPayment(payoutEvent.paymentDate) ||
                  !tokenHasManagePayoutPermission(payoutEvent.productPermissions)) && (
                  <Tooltip
                    maxWidth={250}
                    content={
                      !tokenHasManagePayoutPermission(payoutEvent.productPermissions) ? (
                        <Translate name="studioPayoutEventsTable.noEditPermission" />
                      ) : (
                        <Translate name="studioPayoutEventsTable.paymentDatePassedEdit" />
                      )
                    }
                  >
                    <Button
                      variant="secondary"
                      onClick={() => onEditButtonClick(payoutEvent)}
                      size="xsmall"
                      disabled={true}
                    >
                      <Translate name="common.edit" />
                    </Button>
                  </Tooltip>
                )}
              </ActionButtons>
            ),
            alignContent: 'left',
            width: activeLocale === 'de' ? 195 : 140,
          },
        ]}
      />
    </div>
  );
};

export default PayoutEventsTable;
